import { ChangeEvent, JSX, MouseEvent } from 'react';
import { Row, Col, Form, InputGroup, DropdownButton, Dropdown } from 'react-bootstrap';
import { HiOutlinePlus } from 'react-icons/hi';
import { MdDelete, MdArrowCircleRight } from 'react-icons/md';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import { WtoAdditionalUtm, WtoRule, WtoUtmOperation, WtoVariant } from '../../wto-form-context';
import { UTM_OPERATION } from './rules-form-modal-constants';

interface UrlParamsSectionProps {
  newRule: WtoRule;
  wtoFormObj: Plover;
  readOnly: boolean;
  onInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    valueType?: string,
    idx?: number
  ) => void;
  onSelectChange: (
    event: unknown,
    keyName?: 'additional_utm' | 'additional_utm_operation' | 'variant_id' | 'cookie_operation',
    index?: number,
    index2?: number
  ) => void;
  onDeleteOperationClick: (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => void;
  addParameterUpdatesRow: (ruleType: string) => void;
  addParameterOperation: (index: number, typeblock: string) => void;
}

export const UrlParamsSection = ({
  newRule,
  wtoFormObj,
  readOnly,
  onInputChange,
  onSelectChange,
  onDeleteOperationClick,
  addParameterUpdatesRow,
  addParameterOperation
}: UrlParamsSectionProps) => {
  const renderUrlParamLogic = (
    utmOptrnObj: WtoUtmOperation,
    operationIndex: number,
    utmIndex: number
  ) => {
    const logicSelections: JSX.Element[] = [];
    switch (utmOptrnObj.operation) {
      case 'Replace':
        logicSelections.push(
          <div key={logicSelections.length} className='logic-selection'>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={utmOptrnObj.utm_key || ''}
                  disabled={readOnly}
                  data-utmindex={utmIndex}
                  data-type='utm_operation'
                  placeholder='Key'
                  onChange={e => onInputChange(e, 'utm_key', operationIndex)}
                />
                <Form.Control
                  value={utmOptrnObj.utm_value || ''}
                  disabled={readOnly}
                  data-utmindex={utmIndex}
                  data-type='utm_operation'
                  placeholder='Value'
                  onChange={e => onInputChange(e, 'utm_value', operationIndex)}
                />
              </InputGroup>
            </Form.Group>
            <div className='icon-wrapper'>
              <MdArrowCircleRight />
            </div>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={utmOptrnObj.replace_key || ''}
                  disabled={readOnly}
                  data-utmindex={utmIndex}
                  data-type='utm_operation'
                  placeholder='Key'
                  onChange={e => onInputChange(e, 'replace_key', operationIndex)}
                />
                <Form.Control
                  value={utmOptrnObj.replace_value || ''}
                  disabled={readOnly}
                  data-utmindex={utmIndex}
                  data-type='utm_operation'
                  placeholder='Value'
                  onChange={e => onInputChange(e, 'replace_value', operationIndex)}
                />
              </InputGroup>
            </Form.Group>
          </div>
        );
        break;
      case 'Add':
      case 'Delete':
      case 'Override':
        logicSelections.push(
          <div key={logicSelections.length} className='logic-selection'>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={utmOptrnObj.utm_key || ''}
                  disabled={readOnly}
                  data-utmindex={utmIndex}
                  data-type='utm_operation'
                  placeholder='Key'
                  onChange={e => onInputChange(e, 'utm_key', operationIndex)}
                />
                <Form.Control
                  value={utmOptrnObj.utm_value || ''}
                  disabled={readOnly}
                  data-utmindex={utmIndex}
                  data-type='utm_operation'
                  placeholder='Value'
                  onChange={e => onInputChange(e, 'utm_value', operationIndex)}
                />
              </InputGroup>
            </Form.Group>
          </div>
        );
        break;
      default:
        break;
    }
    return logicSelections;
  };

  const getAdditionalUtmVariantId = (addUtm: WtoAdditionalUtm) => {
    if (addUtm.additional_utm_url.length === 0) return '';
    if (addUtm.additional_utm_url.length === 1) return addUtm.additional_utm_url[0].utm_variant_id;
    return 'All';
  };

  const renderUrlParams = () => {
    // Plover.variants is only weakly typed (Record<string, any>[]) in bpm-types; every
    // variant read off wtoFormObj is actually WtoVariant-shaped at runtime.
    const variantList: WtoVariant[] = [
      ...(wtoFormObj.variants as WtoVariant[]),
      { variant_id: 'All', variant_url: '' }
    ];
    return (
      <div className='operation-section utmSection'>
        {newRule.additional_utm.map((variant: WtoAdditionalUtm, index: number) => {
          const selectedVariants = newRule.additional_utm.map(getAdditionalUtmVariantId);
          const dropdownTitle = getAdditionalUtmVariantId(variant) || 'Select Variant';
          return (
            <div key={index}>
              <hr />
              <div className='variant-wrapper'>
                Select Variant for UTM Parameter Updates
                <DropdownButton
                  className='condition-dropdown'
                  disabled={readOnly}
                  title={dropdownTitle}
                >
                  {variantList.map((variantOption: WtoVariant) =>
                    // check if variant has already been selected in additional_utm
                    // only allow a variant as an option if it has not been selected
                    selectedVariants.includes(variantOption.variant_id) ? null : (
                      <Dropdown.Item
                        key={variantOption.variant_id}
                        eventKey={variantOption.variant_id}
                        onClick={() => onSelectChange(variantOption, 'additional_utm', index)}
                      >
                        {variantOption.variant_id}
                      </Dropdown.Item>
                    )
                  )}
                </DropdownButton>
                {!readOnly && (
                  <IconButton
                    data-index={index}
                    onClick={e => {
                      e.preventDefault();
                      onDeleteOperationClick(e, 'additional_utm');
                    }}
                  >
                    <MdDelete />
                  </IconButton>
                )}
              </div>
              <div className='operation-wrapper'>
                {variant.additional_utm_url.length > 0 && (
                  <>
                    {variant.additional_utm_operation.map((_: unknown, idj: number) => (
                      <div className='each-operation' key={idj}>
                        <DropdownButton
                          disabled={readOnly}
                          className='condition-dropdown'
                          title={
                            variant.additional_utm_operation[idj].operation
                              ? variant.additional_utm_operation[idj].operation
                              : 'Select Operation'
                          }
                        >
                          {UTM_OPERATION.map(utmOperation => (
                            <Dropdown.Item
                              key={utmOperation}
                              eventKey={utmOperation}
                              onClick={() =>
                                onSelectChange(utmOperation, 'additional_utm_operation', idj, index)
                              }
                            >
                              {utmOperation}
                            </Dropdown.Item>
                          ))}
                        </DropdownButton>
                        {variant.additional_utm_operation[idj].operation &&
                          variant.additional_utm_operation[idj].operation.length > 0 &&
                          renderUrlParamLogic(variant.additional_utm_operation[idj], idj, index)}
                        {!readOnly && (
                          <IconButton
                            data-index={index}
                            onClick={e => {
                              e.preventDefault();
                              onDeleteOperationClick(e, 'additional_utm_operation', idj);
                            }}
                          >
                            <MdDelete />
                          </IconButton>
                        )}
                      </div>
                    ))}
                    <Button
                      size={Size.LG}
                      disabled={readOnly}
                      className='add-optrn'
                      leadingElement={<HiOutlinePlus />}
                      onClick={e => {
                        e.preventDefault();
                        addParameterOperation(index, 'additional_utm_operation');
                      }}
                    >
                      Add Operation
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Row>
      <Form.Group as={Col} className='rule-editor utm-cookie-wrapper'>
        <span className='rule-header'>URL Parameter Updates</span>
        {renderUrlParams()}
        {newRule.additional_utm.length > 0 && <hr />}
        {newRule.additional_utm.length < wtoFormObj.variants.length + 1 && (
          <Button
            size={Size.LG}
            disabled={readOnly}
            className='add-rule'
            leadingElement={<HiOutlinePlus />}
            onClick={e => {
              e.preventDefault();
              addParameterUpdatesRow('additional_utm');
            }}
          >
            Add Row
          </Button>
        )}
      </Form.Group>
    </Row>
  );
};
